import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'rec_saved_event_ids';

function loadSavedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

type SavedEventsContextValue = {
  savedIds: string[];
  isSaved: (id: string) => boolean;
  addSaved: (id: string) => void;
  removeSaved: (id: string) => void;
  toggleSaved: (id: string) => void;
};

const SavedEventsContext = createContext<SavedEventsContextValue | null>(null);

export function SavedEventsProvider({ children }: { children: React.ReactNode }) {
  const [savedIds, setSavedIds] = useState<string[]>(loadSavedIds);

  const isSaved = useCallback((id: string) => savedIds.includes(id), [savedIds]);
  const addSaved = useCallback((id: string) => {
    setSavedIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      saveIds(next);
      return next;
    });
  }, []);
  const removeSaved = useCallback((id: string) => {
    setSavedIds((prev) => {
      const next = prev.filter((x) => x !== id);
      saveIds(next);
      return next;
    });
  }, []);
  const toggleSaved = useCallback((id: string) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      saveIds(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ savedIds, isSaved, addSaved, removeSaved, toggleSaved }),
    [savedIds, isSaved, addSaved, removeSaved, toggleSaved]
  );

  return (
    <SavedEventsContext.Provider value={value}>
      {children}
    </SavedEventsContext.Provider>
  );
}

export function useSavedEvents() {
  const ctx = useContext(SavedEventsContext);
  if (!ctx) throw new Error('useSavedEvents must be used within SavedEventsProvider');
  return ctx;
}
